<template>
    <div class="ctc-settings-general" style="min-height: 400px;">
        <fieldset :disabled="!canEdit" style="border: none; padding: 0; margin: 0;">
            <!-- CTC Configuration Mode -->
            <h4 class="ctc-dashboard-title mb-1">CTC Configuration Mode</h4>
            <div class="ctc-dashboard-subtitle">Choose how you want to configure CTC items</div>

            <div class="mode-card-row">
                <!-- The tick marks the mode currently saved in settings, not the
                     one highlighted: picking the other card moves the highlight
                     and the tick follows only once the activation switch below
                     saves that mode. -->
                <div class="mode-card" :class="{ 'is-selected': configMode === 1 }" @click="selectMode(1)">
                    <div class="mode-card-title">Item-wise Settings</div>
                    <div class="mode-card-sub">Configure CTC items individually through the interface</div>
                    <b-icon v-if="savedMode === 1" icon="check-circle-fill" class="mode-card-check"></b-icon>
                </div>
                <div class="mode-card" :class="{ 'is-selected': configMode === 2 }" @click="selectMode(2)">
                    <div class="mode-card-title">Upload</div>
                    <div class="mode-card-sub">Bulk upload CTC items via CSV or Excel files</div>
                    <b-icon v-if="savedMode === 2" icon="check-circle-fill" class="mode-card-check"></b-icon>
                </div>
            </div>

            <div class="mode-divider"></div>

            <!-- Activation switch. Reads on when the highlighted card is the mode
                 already saved in settings; picking the other card turns it off,
                 and clicking it saves that mode (and reloads, as a mode change
                 rebuilds the whole tab strip). Until then the panel below stays
                 locked, so nothing is edited against a mode that isn't live. -->
            <div class="mode-status-bar"
                 :class="{ 'is-on': activationOn, 'is-clickable': canToggleActivation }"
                 @click="toggleActivation">
                <span class="mode-status-label">Activate Configuration Mode: {{ selectedModeLabel }}</span>
                <div class="mode-status-switch">
                    <span class="mode-status-state">Inactive</span>
                    <span class="ctc-switch" :class="{ 'is-on': activationOn }">
                        <span class="ctc-switch-knob"></span>
                    </span>
                    <span class="mode-status-state">Active</span>
                </div>
            </div>

            <!-- General Settings (Item-wise mode) -->
            <div v-if="configMode !== 2" class="settings-panel">
                <h5 class="panel-heading">General Settings</h5>

                <fieldset class="panel-fieldset" :disabled="!activationOn">
                    <div class="field-row">
                        <div class="field-col field-col-grow">
                            <label class="field-label">Starting Month of the year <span class="text-danger">*</span></label>
                            <b-form-select v-model="startMonth" class="ctc-field" :options="months"></b-form-select>
                            <div class="field-hint">Select the starting month for CTC calculation cycle</div>
                        </div>
                        <div class="field-col field-col-gf">
                            <label class="field-label">Enable Gratuity Fund (GF)</label>
                            <div class="gf-radio-row">
                                <div class="gf-radio" @click="canEditSettings && (gfEnabled = 1)">
                                    <span class="gf-radio-circle" :class="{ 'is-checked': gfEnabled === 1 }"></span>
                                    <span class="gf-radio-label">Yes</span>
                                </div>
                                <div class="gf-radio" @click="canEditSettings && (gfEnabled = 0)">
                                    <span class="gf-radio-circle" :class="{ 'is-checked': gfEnabled === 0 }"></span>
                                    <span class="gf-radio-label">No</span>
                                </div>
                            </div>
                            <div class="field-hint">Toggle GF calculation on or off</div>
                        </div>
                    </div>

                    <div class="field-row" v-if="gfEnabled">
                        <div class="field-col field-col-grow">
                            <label class="field-label">GF Based On</label>
                            <b-form-select v-model="gfBasedOn" class="ctc-field" :options="gfBasedOnOptions"></b-form-select>
                            <div class="field-hint">Select the salary component GF is calculated on</div>
                        </div>
                        <div class="field-col field-col-grow">
                            <label class="field-label">GF Percentage (%)</label>
                            <b-form-input type="number" v-model="gfPercentage" class="ctc-field" placeholder="10" step="0.01"></b-form-input>
                            <div class="field-hint">Enter the percentage for GF calculation</div>
                        </div>
                    </div>
                </fieldset>
            </div>

            <!-- Manual/Upload Heads Table (Upload mode) -->
            <div v-else class="settings-panel">
                <div class="panel-header-row">
                    <h5 class="panel-heading mb-0">Manual CTC Items</h5>
                    <span class="total-badge">Total: {{ manualHeads.length }}</span>
                </div>

                <div class="table-container-custom table-responsive">
                    <table class="table mb-0 custom-report-table manual-items-table">
                        <thead class="text-white custom-thead">
                            <tr>
                                <th class="text-center align-middle">Item Name</th>
                                <th class="text-center align-middle">Type</th>
                                <th class="text-center align-middle">Allowed in other</th>
                                <th class="text-center align-middle">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="head in visibleHeads" :key="head.id">
                                <td class="align-middle">{{ head.name }}</td>
                                <td class="align-middle">{{ getTypeText(head.type) }}</td>
                                <td class="align-middle">
                                    <div class="allowed-cell" @click="toggleAllowedOther(head)">
                                        <span class="ctc-switch" :class="{ 'is-on': head.allowed_other_part === 1 }">
                                            <span class="ctc-switch-knob"></span>
                                        </span>
                                        <span class="allowed-label">
                                            {{ head.type === 'compensation' ? 'Allowed in Annual' : 'Allowed in Compensation' }}
                                        </span>
                                    </div>
                                </td>
                                <td class="align-middle text-center">
                                    <span class="status-pill" v-if="head.status === 'active'">
                                        <b-icon icon="check-circle-fill"></b-icon> Active
                                    </span>
                                    <span class="status-pill is-inactive" v-else>
                                        <b-icon icon="dash-circle-fill"></b-icon> Inactive
                                    </span>
                                </td>
                            </tr>
                            <tr v-if="!activationOn">
                                <td colspan="4" class="text-center py-5 text-muted">
                                    <b-icon icon="info-circle" scale="1.5" class="mb-3 text-secondary d-block mx-auto"></b-icon>
                                    Activate the configuration via toggle button to show the Manual CTC Items
                                </td>
                            </tr>
                            <tr v-else-if="manualHeads.length === 0">
                                <td colspan="4" class="text-center py-5 text-muted">
                                    <b-icon icon="info-circle" scale="1.5" class="mb-3 text-secondary d-block mx-auto"></b-icon>
                                    No manual/non-automated CTC items found.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Plain system pagination markup so it inherits the shared
                     `.ess-portal` rules and matches every other CTC list. -->
                <div class="row py-2 no-margin" v-if="activationOn && manualHeads.length > 0">
                    <div class="col-md-12 p-0 no-margin">
                        <ul class="pagination b-pagination float-left mb-0 mt-1">
                            <li class="page-item disabled m-2">View</li>
                            <li class="page-item disabled">
                                <b-form-select v-model="perPage" :options="lengthOptions" @change="currentPage = 1"></b-form-select>
                            </li>
                            <li class="page-item disabled m-2">per page. Total {{ manualHeads.length }} records found.</li>
                        </ul>
                        <b-pagination-nav class="float-right mt-1"
                                          v-model="currentPage"
                                          :number-of-pages="totalPages" prev-text="Previous"
                                          next-text="Next" base-url="#" first-number
                                          last-number></b-pagination-nav>
                    </div>
                </div>
            </div>
        </fieldset>

        <div class="apply-row" v-if="canEdit">
            <!-- Same treatment as "Generate Projection" on the Annual CTC
                 Reports panel: the plain system button, styled globally in
                 resources/sass/atlhr.scss. -->
            <b-button :disabled="!activationOn" @click="saveSettings">Apply Changes</b-button>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import ctcOverlay from '../js/ctcOverlay';
export default {
    mixins: [ctcOverlay],
    name: "CtcSettingsGeneral",
    props: {
        ctcSettings: {
            type: Object,
            default: null
        }
    },
    watch: {
        ctcSettings: {
            immediate: true,
            handler(newSettings) {
                if (newSettings) {
                    this.applyCtcSettings(newSettings);
                }
            }
        },
        isSaving(newVal) {
            this.ctcOverlay(newVal);
        },
        // Shrinking the list (or growing the page size) can strand the viewer on
        // a page that no longer exists, which renders an empty table.
        totalPages(pages) {
            if (this.currentPage > pages) {
                this.currentPage = pages;
            }
        }
    },
    data() {
        return {
            isSaving: false,
            configMode: 1,
            initialConfigMode: null,
            startMonth: 1,
            gfEnabled: 0,
            gfBasedOn: 'basic_salary',
            gfPercentage: 0,
            heads: [],
            // `allowed_other_part` as it stands on the server, keyed by head id.
            // The table toggles only mutate `heads`; diffing against this snapshot
            // is what turns them into a payload when Apply Changes is pressed.
            allowedSnapshot: {},
            currentPage: 1,
            perPage: 10,
            lengthOptions: [10, 25, 50, 100],
            gfBasedOnOptions: [
                { value: 'basic_salary', text: 'Basic Salary' },
                { value: 'gross_salary', text: 'Gross Salary' }
            ],
            months: [
                { value: 1, text: 'January' },
                { value: 2, text: 'February' },
                { value: 3, text: 'March' },
                { value: 4, text: 'April' },
                { value: 5, text: 'May' },
                { value: 6, text: 'June' },
                { value: 7, text: 'July' },
                { value: 8, text: 'August' },
                { value: 9, text: 'September' },
                { value: 10, text: 'October' },
                { value: 11, text: 'November' },
                { value: 12, text: 'December' }
            ]
        };
    },
    computed: {
        canEdit() {
            return !!this.$acl && this.$acl['cost-to-company'] == 2;
        },
        selectedModeLabel() {
            return this.configMode === 2 ? 'Upload' : 'Item-wise';
        },
        // On once the highlighted mode is the one saved in settings. There is no
        // "no mode" state to switch back to, so an already-active mode's switch
        // is inert — you activate the other one by selecting its card first.
        activationOn() {
            return this.savedMode !== null && this.configMode === this.savedMode;
        },
        canToggleActivation() {
            return this.canEdit && !this.activationOn;
        },
        // Everything below the bar stays locked until the selected mode is live.
        canEditSettings() {
            return this.canEdit && this.activationOn;
        },
        // The mode persisted in settings — captured when the settings load and
        // re-synced after a successful save. Null until the load lands, so no
        // card is ticked before we know which one is live.
        savedMode() {
            return this.initialConfigMode === null ? null : Number(this.initialConfigMode);
        },
        manualHeads() {
            return this.heads.filter(head => !head.is_automated || Number(head.is_automated) === 0);
        },
        // { headId: 0|1 } for every row the viewer has flipped since the last
        // load or save. Empty when the table is untouched, so an ordinary
        // settings save carries no head payload at all.
        pendingAllowedChanges() {
            const changes = {};
            this.manualHeads.forEach(head => {
                const current = Number(head.allowed_other_part) === 1 ? 1 : 0;
                if (current !== this.allowedSnapshot[head.id]) {
                    changes[head.id] = current;
                }
            });
            return changes;
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.manualHeads.length / this.perPage));
        },
        paginatedHeads() {
            const start = (this.currentPage - 1) * this.perPage;
            return this.manualHeads.slice(start, start + this.perPage);
        },
        // The rows are withheld until Upload is the active mode; the table then
        // carries the "activate first" placeholder in place of its empty state.
        visibleHeads() {
            return this.activationOn ? this.paginatedHeads : [];
        }
    },
    mounted() {
        this.loadSettings();
        this.loadHeads();
    },
    methods: {
        selectMode(mode) {
            if (!this.canEdit) {
                return;
            }
            this.configMode = mode;
        },
        // Activating a mode is a save: it goes through the same endpoint as Apply
        // Changes, which then reloads the page because switching modes changes
        // which tabs the module even offers.
        toggleActivation() {
            if (!this.canToggleActivation) {
                return;
            }
            this.saveSettings();
        },
        loadSettings() {
            if (this.ctcSettings) {
                this.applyCtcSettings(this.ctcSettings);
                return;
            }
            axios.get('/cost-to-company/get-settings')
                .then(response => {
                    if (response.data && response.data.ctcSettings) {
                        const settings = response.data.ctcSettings;
                        this.applyCtcSettings(settings);
                    }
                })
                .catch(error => {
                    console.error('Error loading CTC settings:', error);
                });
        },
        loadHeads() {
            axios.get('/cost-to-company/heads')
                .then(response => {
                    if (response.data && response.data.data) {
                        this.heads = response.data.data;
                        this.snapshotAllowed();
                    }
                })
                .catch(error => {
                    console.error('Error loading CTC heads:', error);
                });
        },
        snapshotAllowed() {
            const snapshot = {};
            this.manualHeads.forEach(head => {
                snapshot[head.id] = Number(head.allowed_other_part) === 1 ? 1 : 0;
            });
            this.allowedSnapshot = snapshot;
        },
        applyCtcSettings(settings) {
            this.startMonth = Number(settings.start_month) || 1;
            this.configMode = Number(settings.mode) || 1;
            this.gfEnabled = Number(settings.gf_enabled) || 0;
            this.gfBasedOn = settings.gf_based_on || 'basic_salary';
            this.gfPercentage = settings.gf_percentage || 0;
            if (this.initialConfigMode === null) {
                this.initialConfigMode = this.configMode;
            }
        },
        getTypeText(val) {
            const types = {
                compensation: 'Compensation',
                annual: 'Annual'
            };
            return types[val] || val;
        },
        getFrequencyText(val) {
            const frequencies = {
                yearly: 'Yearly',
                half_yearly: 'Half-Yearly',
                quarterly: 'Quarterly',
                monthly: 'Monthly'
            };
            return frequencies[val] || val;
        },
        saveSettings() {
            if (!this.canEdit) {
                this.$bvToast.toast('You are not authorized to save settings.', { title: 'Error', variant: 'danger', solid: true });
                return;
            }
            this.isSaving = true;
            const formData = new FormData();
            formData.append('start_month', this.startMonth);
            formData.append('mode', this.configMode);
            formData.append('gf_enabled', this.gfEnabled);
            formData.append('gf_based_on', this.gfBasedOn);
            formData.append('gf_percentage', this.gfPercentage);

            const allowedChanges = this.pendingAllowedChanges;
            if (Object.keys(allowedChanges).length) {
                formData.append('allowed_other_part', JSON.stringify(allowedChanges));
            }

            axios.post('/cost-to-company/settings', formData)
                .then(response => {
                    if (response.data && response.data.success) {
                        this.$bvToast.toast(response.data.message || 'Settings saved successfully.', {
                            title: 'Success',
                            variant: 'success',
                            solid: true
                        });
                        if (this.initialConfigMode !== null && Number(this.configMode) !== Number(this.initialConfigMode)) {
                            setTimeout(() => {
                                window.location.reload();
                            }, 300);
                        } else {
                            this.$emit('settings-updated');
                            this.initialConfigMode = Number(this.configMode);
                            // The rows just written are the new baseline, so the
                            // next save doesn't resend them.
                            this.snapshotAllowed();
                            this.isSaving = false;
                        }
                    } else {
                        this.$bvToast.toast('Failed to save settings.', {
                            title: 'Error',
                            variant: 'danger',
                            solid: true
                        });
                        this.isSaving = false;
                    }
                })
                .catch(error => {
                    console.error('Error saving CTC settings:', error);
                    this.isSaving = false;
                });
        },
        // Local only — the row is not written back until Apply Changes, which
        // posts every flipped row with the rest of the settings in one request.
        toggleAllowedOther(head) {
            if (!this.canEditSettings) {
                this.$bvToast.toast('You are not authorized to change settings.', { title: 'Error', variant: 'danger', solid: true });
                return;
            }
            head.allowed_other_part = Number(head.allowed_other_part) === 1 ? 0 : 1;
        }
    }
}
</script>

<style scoped>
/* Page header (.ctc-dashboard-title / .ctc-dashboard-subtitle), the teal table
   head (.custom-thead) and .custom-report-table are styled centrally in
   resources/sass/_ctc.scss. Everything below is specific to this screen. */
.ctc-settings-general {
    font-family: 'Poppins', sans-serif;
}

/* ---- CTC Configuration Mode cards ---- */
.mode-card-row {
    display: flex;
    gap: 16px;
    margin-top: 20px;
}

.mode-card {
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    padding: 20px 40px;
    text-align: center;
    background: #ffffff;
    border: 1px solid #B6E0DD;
    border-radius: 16px;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
}

.mode-card:hover {
    border-color: #3E6C68;
}

.mode-card.is-selected {
    background: #F4FFFA;
    border-color: #3E6C68;
}

.mode-card-title {
    font-weight: 600;
    font-size: 18px;
    line-height: 150%;
    color: #000000;
}

.mode-card-sub {
    font-weight: 500;
    font-size: 15px;
    line-height: 140%;
    color: #747474;
}

.mode-card-check {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 22px;
    height: 22px;
    color: #069651;
}

.mode-divider {
    height: 1px;
    margin: 30px 0;
    background-color: #126D66;
    opacity: 0.2;
}

/* ---- Activate Configuration Mode bar ---- */
.mode-status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 5px 20px;
    min-height: 34px;
    background: #E8E8E8;
    border-radius: 8px;
    transition: background-color 0.2s ease;
}

.mode-status-bar.is-clickable {
    cursor: pointer;
}

.mode-status-bar.is-on {
    background: #EFFCFC;
}

.mode-status-label {
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.01em;
    color: #8F8F8F;
}

.mode-status-bar.is-on .mode-status-label {
    color: #01524B;
}

.mode-status-switch {
    display: flex;
    align-items: center;
    gap: 5px;
}

.mode-status-state {
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: -0.01em;
    color: #01524B;
}

/* ---- Switch shared by the activation bar and the "Allowed in other" column ---- */
.ctc-switch {
    position: relative;
    display: inline-block;
    flex-shrink: 0;
    width: 36px;
    height: 20px;
    border-radius: 10px;
    background: #D5E6EB;
    transition: background-color 0.2s ease;
}

.ctc-switch.is-on {
    background: #01524B;
}

.ctc-switch-knob {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #ffffff;
    transition: transform 0.2s ease;
}

.ctc-switch.is-on .ctc-switch-knob {
    transform: translateX(16px);
}

/* The bar's own switch reads red while the mode is off, per the design. */
.mode-status-bar .ctc-switch {
    background: #CC5057;
}

.mode-status-bar .ctc-switch.is-on {
    background: #01524B;
}

/* ---- Panels (General Settings / Manual CTC Items) ---- */
.settings-panel {
    margin-top: 30px;
    padding: 30px;
    background: #ffffff;
    border: 1px solid #D9D9D9;
    border-radius: 16px;
}

.panel-heading {
    font-weight: 600;
    font-size: 22px;
    line-height: 1.2;
    letter-spacing: -0.02em;
    color: #000D21;
    margin-bottom: 30px;
}

/* Wraps the General Settings fields so they can be locked until the mode is
   activated. `<fieldset disabled>` blocks the inputs on its own; the dimming is
   what makes the lock legible. */
.panel-fieldset {
    border: none;
    padding: 0;
    margin: 0;
    min-width: 0;
}

.panel-fieldset:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.panel-fieldset:disabled .gf-radio {
    cursor: not-allowed;
}

.panel-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-bottom: 25px;
}

.total-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 95px;
    height: 31px;
    padding: 0 12px;
    background: #01524B;
    border-radius: 8px;
    font-weight: 500;
    font-size: 14px;
    letter-spacing: -0.02em;
    color: #ffffff;
}

/* ---- Form fields ---- */
.field-row {
    display: flex;
    align-items: flex-start;
    gap: 30px;
}

.field-row + .field-row {
    margin-top: 30px;
}

.field-col {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}

.field-col-grow {
    flex: 1 1 0;
}

.field-col-gf {
    flex: 0 0 315px;
    max-width: 315px;
}

.field-label {
    font-weight: 600;
    font-size: 18px;
    line-height: 27px;
    letter-spacing: -0.02em;
    color: #000000;
    margin-bottom: 0;
}

.field-hint {
    font-weight: 500;
    font-size: 15px;
    line-height: 24px;
    letter-spacing: -0.02em;
    color: #747474;
}

/* `<b-form-select>` / `<b-form-input>` put the class straight on the rendered
   <select>/<input>, so these are plain (non-deep) scoped rules. */
.ctc-field {
    height: 52px !important;
    padding: 8px 16px !important;
    background-color: #ffffff !important;
    border: 1px solid #004242 !important;
    border-radius: 8px !important;
    font-weight: 500;
    font-size: 16px;
    letter-spacing: -0.02em;
    color: #34444C !important;
    box-shadow: none !important;
}

/* Leaves room for Bootstrap's caret, which `.custom-select` draws as a
   background image in the right-hand padding. */
select.ctc-field {
    padding-right: 40px !important;
}

/* The figma draws number fields as plain boxes, so drop the native spinner. */
input.ctc-field[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
}

input.ctc-field[type="number"]::-webkit-outer-spin-button,
input.ctc-field[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.ctc-field:focus {
    border-color: #004242 !important;
    box-shadow: 0 0 0 3px rgba(0, 66, 66, 0.12) !important;
}

.ctc-field:disabled {
    background-color: #ffffff !important;
    opacity: 0.7;
    cursor: not-allowed;
}

/* ---- GF radios ---- */
.gf-radio-row {
    display: flex;
    align-items: center;
    gap: 29px;
    height: 52px;
}

.gf-radio {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

.gf-radio-circle {
    position: relative;
    width: 17px;
    height: 17px;
    flex-shrink: 0;
    border: 2px solid #0060C9;
    border-radius: 50%;
    background: #ffffff;
}

.gf-radio-circle.is-checked::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 1px;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: #0060C9;
}

.gf-radio-label {
    font-weight: 500;
    font-size: 18px;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: #747474;
}

/* ---- Manual CTC Items table ---- */
/* Overrides the shared `border-collapse: separate`: the design draws a full
   1px grid, and separate borders would double up on every shared edge. The
   rounded corners come from the `.table-container-custom` wrapper instead. */
.manual-items-table {
    border-collapse: collapse;
}

.manual-items-table thead th {
    padding: 10px;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.02em;
    border: none;
}

.manual-items-table tbody td {
    padding: 14px 10px;
    border: 1px solid #E8E8E8;
    font-weight: 400;
    font-size: 15px;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: #34444C;
}

/* Zebra striping, set on the row (not the cell) so the shared
   `.custom-report-table tbody tr:hover` rule in _ctc.scss still paints over it. */
.manual-items-table tbody tr:nth-child(even) {
    background-color: #F8F8F8;
}

.allowed-cell {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
}

.allowed-label {
    font-weight: 400;
    font-size: 15px;
    color: #34444C;
}

.status-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-width: 111px;
    padding: 6px 10px;
    background: #F2FAF5;
    border: 1px solid #BDDBCE;
    border-radius: 4px;
    font-weight: 500;
    font-size: 15px;
    letter-spacing: -0.02em;
    color: #46524D;
}

.status-pill .b-icon {
    color: #069651;
    font-size: 15px;
}

.status-pill.is-inactive {
    background: #ffffff;
    border-color: #747474;
    color: #747474;
}

.status-pill.is-inactive .b-icon {
    color: #747474;
}

/* ---- Apply Changes ---- */
.apply-row {
    display: flex;
    justify-content: center;
    margin-top: 30px;
}

@media (max-width: 991px) {
    .mode-card-row,
    .field-row {
        flex-direction: column;
    }

    .field-col-gf {
        flex: 1 1 auto;
        max-width: none;
    }
}
</style>
